/**
 * **Exploding Kittens 的 zod schema** —— 线上跑的那三种载荷。
 *
 * `core/` 是纯逻辑，不认识 zod；`net/` 是通用的，不认识牌。
 * 这一层是**两者之间唯一的那张皮**。
 *
 * ## 这些 schema 的职责是什么（以及不是什么）
 *
 * ⚠️ **不是为了把类型再抄一遍。** 抄一遍的价值有限，成本却不低
 * （15 种动作 + 25 种事件 + 一整套视图）。
 *
 * 它们要挡的是两类**真会出的事**：
 *
 * 1. **自家版本的 bug**：字段改名、结构变了、半路上线了两份不同的构建
 * 2. **对端崩溃**：`reducer` 会 `action.play.cards.map(...)`，
 *    少一个字段就是一个 `TypeError`，而它发生在**房主**身上 ——
 *    房主一崩整局就没了
 *
 * 所以判据是：**凡是 reducer / 渲染层会直接解引用的地方，都要有 schema 兜住。**
 * 至于"这个牌名存不存在""这个动作合不合法"，那是 `reducer` 的活
 * （它返回 `{ok:false, reason}`，不崩）。
 *
 * ## `looseObject` 的用法
 *
 * 视图这边故意**宽松**：多出来的字段不拒绝。理由是视图要跟着游戏演化
 * （加一个 HUD 字段不该让老客户端把整包丢掉），而视图的正确性另有
 * **哈希校验**兜底（`docs/netcode-s3.md` §6）——
 * 那一层比 schema 严得多，也准得多。
 */

import { z } from 'zod';

/** `PlayerId` / `CardUid` / `CardTitle` 都是 branded string/number，线上就是它们本身 */
export const PlayerIdSchema = z.string().min(1);
export const CardUidSchema = z.number().int().nonnegative();
export const CardTitleSchema = z.string().min(1);

// ============================================================
// 动作
// ============================================================

const PlayIntentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('single'),
    card: CardUidSchema,
    // ⚠️ `reducer` 会读它，所以类型要对
    target: PlayerIdSchema.nullish(),
  }),
  z.object({
    kind: z.literal('pair'),
    cards: z.tuple([CardUidSchema, CardUidSchema]),
    target: PlayerIdSchema,
  }),
  z.object({
    kind: z.literal('triple'),
    cards: z.tuple([CardUidSchema, CardUidSchema, CardUidSchema]),
    target: PlayerIdSchema,
    namedTitle: CardTitleSchema,
  }),
  z.object({
    kind: z.literal('fiveDifferent'),
    cards: z.array(CardUidSchema).min(2),
    discardPick: CardUidSchema,
  }),
]);

const DecisionInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('insertLethal'), index: z.number().int() }),
  z.object({ kind: z.literal('useRescue'), use: z.boolean() }),
  z.object({ kind: z.literal('giveCard'), card: CardUidSchema }),
  z.object({
    kind: z.literal('stealCard'),
    index: z.number().int(),
    // 被抽的人报上来的最终顺序。**合法性由 reducer 判**（要是那一手牌的排列）
    order: z.array(CardUidSchema).optional(),
  }),
  z.object({ kind: z.literal('pickFromDiscard'), card: CardUidSchema }),
]);

/**
 * 上行动作。
 *
 * ⚠️ `actor` 一定在，但**它不可信** —— 房主拿 `ctx.from`（网络层认定的身份）
 * 去和它比对，见 `src/host/rules.ts`。schema 只保证"有这个字段"。
 */
export const GameActionSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('lobby/join'), actor: PlayerIdSchema, name: z.string() }),
  z.object({ t: z.literal('lobby/leave'), actor: PlayerIdSchema }),
  z.object({ t: z.literal('lobby/setConfig'), actor: PlayerIdSchema, config: z.looseObject({}) }),
  z.object({ t: z.literal('game/start'), actor: PlayerIdSchema, seed: z.number().int() }),
  z.object({
    t: z.literal('turn/play'),
    actor: PlayerIdSchema,
    play: PlayIntentSchema,
  }),
  z.object({
    t: z.literal('turn/arm'),
    actor: PlayerIdSchema,
    play: PlayIntentSchema,
  }),
  z.object({ t: z.literal('turn/arm/cancel'), actor: PlayerIdSchema }),
  z.object({ t: z.literal('turn/draw'), actor: PlayerIdSchema }),
  z.object({ t: z.literal('window/nope'), actor: PlayerIdSchema, card: CardUidSchema }),
  z.object({ t: z.literal('window/timeout'), actor: PlayerIdSchema }),
  z.object({ t: z.literal('decide'), actor: PlayerIdSchema, decision: DecisionInputSchema }),
  z.object({
    t: z.literal('hand/reorder'),
    actor: PlayerIdSchema,
    from: z.number().int(),
    to: z.number().int(),
  }),
  z.object({ t: z.literal('net/connected'), actor: PlayerIdSchema }),
  z.object({ t: z.literal('net/disconnected'), actor: PlayerIdSchema }),
]);
export type WireAction = z.infer<typeof GameActionSchema>;

// ============================================================
// 事件
// ============================================================

/**
 * 下行事件。
 *
 * ⚠️ 事件**只给动画/音效**（`architecture.md` §3.6），所以 schema 可以宽松 ——
 * 一条坏事件最多让动画不好看，不会让状态分叉。
 * 但仍然要挡住"少字段导致渲染层 `TypeError`"这一类。
 */
export const GameEventSchema = z.looseObject({
  t: z.string().min(1),
  player: PlayerIdSchema.optional(),
  by: PlayerIdSchema.optional(),
  from: PlayerIdSchema.optional(),
  to: PlayerIdSchema.optional(),
  card: CardUidSchema.optional(),
  cards: z.array(CardUidSchema).optional(),
  title: CardTitleSchema.optional(),
  name: z.string().optional(),
  ready: z.boolean().optional(),
  count: z.number().int().optional(),
  turns: z.number().int().optional(),
  turnsRemaining: z.number().int().optional(),
  winner: PlayerIdSchema.optional(),
  cancelled: z.boolean().optional(),
  secret: z.boolean().optional(),
  kind: z.string().optional(),
  target: PlayerIdSchema.nullish(),
  index: z.number().int().optional(),
});
export type WireEvent = z.infer<typeof GameEventSchema>;

// ============================================================
// 视图
// ============================================================

const VisibleCardSchema = z.object({ uid: CardUidSchema, title: CardTitleSchema });

const PlayerViewPlayerSchema = z.object({
  id: PlayerIdSchema,
  name: z.string(),
  alive: z.boolean(),
  connected: z.boolean(),
  handCount: z.number().int().nonnegative(),
});

const KnownCardSchema = z.object({
  indexFromTop: z.number().int().nonnegative(),
  title: CardTitleSchema,
});
const KnownInDrawSchema = z.object({ title: CardTitleSchema, count: z.number() });
const KnownInHandSchema = z.object({
  player: PlayerIdSchema,
  title: CardTitleSchema,
  count: z.number(),
});

/**
 * `Decision` 的**客户端**形态。
 *
 * ⚠️ 只有本人拿得到内容（`yourDecision`），别人那边是 `null`。
 * 所以这里的 schema 宽松一点没关系 —— 它是"本人自己那份"。
 */
const DecisionSchema = z.looseObject({ kind: z.string().min(1) });

/**
 * `PlayerView`。
 *
 * ⚠️ **宽松**（`looseObject`）。视图会跟着游戏演化，而多一个字段不该让
 * 老客户端把整包丢掉；真正的正确性由**视图哈希**兜底（`net/hash.ts`）。
 * 这里只钉住"渲染层会直接读的那些字段"。
 */
export const PlayerViewSchema = z.looseObject({
  you: PlayerIdSchema,
  phase: z.string().min(1),
  host: PlayerIdSchema,
  config: z.looseObject({}),
  winner: PlayerIdSchema.nullish(),
  players: z.array(PlayerViewPlayerSchema),
  drawPileCount: z.number().int().nonnegative(),
  discardPile: z.array(VisibleCardSchema),
  turn: z
    .looseObject({
      player: PlayerIdSchema,
      turnsRemaining: z.number().int(),
      fromAttack: z.boolean(),
    })
    .nullish(),
  resolution: z
    .looseObject({
      play: z.looseObject({
        player: PlayerIdSchema,
        cards: z.array(VisibleCardSchema),
        kind: z.string(),
        target: PlayerIdSchema.nullish(),
      }),
      nopeChain: z.array(z.looseObject({ player: PlayerIdSchema, card: VisibleCardSchema })),
      cancelledIfClosedNow: z.boolean(),
    })
    .nullish(),
  pending: z
    .looseObject({
      player: PlayerIdSchema,
      kind: z.string(),
      revealedTitle: CardTitleSchema.nullish(),
      steal: z.looseObject({ from: PlayerIdSchema, count: z.number().int() }).nullish(),
    })
    .nullish(),
  yourHand: z.array(VisibleCardSchema),
  yourDecision: DecisionSchema.nullish(),
  knownTop: z.array(KnownCardSchema),
  knownInDraw: z.array(KnownInDrawSchema),
  knownInHand: z.array(KnownInHandSchema),
});
